
import { mergeMap, take } from 'rxjs/operators'
import { Epic } from 'interfaces/middleware'
import { LOAD_RECAPTCHA_API, EXECUTE_RECAPTCHA, loadRecaptchaAPIActionCreator } from 'actions/recaptchaActions'
import { empty, from, of } from 'rxjs'
import { RecaptchaWindow } from 'interfaces/DOM'
import { captureException, captureWarning } from 'helpers/errorHelpers'
import { Store } from 'interfaces/state'
import { combineEpics } from 'redux-observable'
import { recaptchaExecutedActionCreator } from 'actions/recaptchaActions'
import { isFeatureEnabled } from 'helpers/featureHelpers'
import { recaptchaIsFailedToLoadSelector, recaptchaWidgetByPlacementSelector } from 'selectors/recaptchaSelectors'
import { FORM_SUBMIT_FAILED } from 'actions/formActions'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { end } from 'rxOperators/end'
import { catchSafe } from 'rxOperators/catchSafe'

declare const window: RecaptchaWindow

const RECAPTCHA_READY_CALLBACK = 'onRecaptchaReady'
const RECAPTCHA_SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?onload=${ RECAPTCHA_READY_CALLBACK }&render=explicit`

const loadRecaptchaEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(LOAD_RECAPTCHA_API, 'request')).pipe(
      mergeMap(() => {
        if (store.getState().app.recaptcha.status === 'loaded') {
          return empty()
        }

        const recaptchaReadyPromise = new Promise<void>((resolve, reject) => {
          if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
            resolve()
            return
          }

          window[RECAPTCHA_READY_CALLBACK] = resolve

          const script = document.createElement('script')
          script.src = RECAPTCHA_SCRIPT_SRC
          script.async = true
          script.onerror = () => {
            const error = new Error('reCaptcha script loading error')
            captureException(error)
            reject(error)
          }
          document.head.appendChild(script)
        })

        const { success, fail } = loadRecaptchaAPIActionCreator

        return from(recaptchaReadyPromise).pipe(
          mergeMap(() => of(success({}, {}))))
          .pipe(catchSafe(() => of(fail({}))))
      }))

const failedToLoadRecaptchaEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(LOAD_RECAPTCHA_API, 'fail')).pipe(
      take(2),
      mergeMap(() => {
        return of(loadRecaptchaAPIActionCreator.request({}))
      }))

const executeRecaptchaEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(byType(EXECUTE_RECAPTCHA)).pipe(
      mergeMap(({ payload }) => {
        const state = store.getState()
        const isFailed = recaptchaIsFailedToLoadSelector(state)

        if (isFailed) {
          return empty()
        }

        const { placement } = payload
        const widget = recaptchaWidgetByPlacementSelector(state, { placement })
        const { widgetID, callback } = widget

        if (isFeatureEnabled('autotests')) {
          callback('test')
        }

        const response = window.grecaptcha.getResponse(widgetID)

        // FRO-1523 how to reproduce:
        // - fill up the register form but enter not matching passwords
        // - submit form by pressing Enter.
        // Recaptcha is executed but then form async validation marks the form as invalid.
        // - fix passwords and submit the form again.
        // Recaptcha is executed but does not fire the callback because its response hasn't changed.
        if (response) {
          callback(response)

          captureWarning('already have reCAPTCHA response')
          return empty()
        }

        window.grecaptcha.execute(widgetID)

        return of(recaptchaExecutedActionCreator(placement))
      }))

const recaptchaFormSubmitErrorEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(byType(FORM_SUBMIT_FAILED))
    .pipe(end(({ payload }) => {
      const placement = payload.formName
      const state = store.getState()
      const widget = recaptchaWidgetByPlacementSelector(state, { placement })

      if (widget) {
        const { widgetID } = widget
        window.grecaptcha.reset(widgetID)
      }
    }))

export default combineEpics(
  failedToLoadRecaptchaEpic,
  loadRecaptchaEpic,
  executeRecaptchaEpic,
  recaptchaFormSubmitErrorEpic,
)
